// =============================================================
// Sync state context (Phase 9.3) — drives the per-record sync badges
// (CACHED OFFLINE / SYNC PENDING) and the topbar connectivity dot.
//
// HONEST SCOPE / LIVE DEPENDENCY:
//   The product's offline store is PowerSync over SQLite (core/sync.SyncClient).
//   LIVE replication needs the DEPLOYED PowerSync service (Phase-8 infra) + the
//   @powersync/web factory — see lib/powersync.ts, which throws by design until
//   then. So in-sandbox we CANNOT read a real `ps_crud` queue. Rather than fake
//   replication, this context derives badge state from what IS available in the
//   browser today:
//     - `online`        ← navigator.onLine (+ online/offline events) — the same
//                          probe core's SyncClient.defaultOnlineProbe() uses.
//     - `pendingKeys`   ← a local in-memory set of record keys with a not-yet-
//                          flushed local mutation. When the live SyncClient is
//                          wired (Phase 8) this set is replaced by a subscription
//                          to SyncClient.onStatusChange() / the CRUD queue; the
//                          BADGE COMPONENT contract does not change.
//
//   The result: badges reflect REAL connectivity now, and the pending-mutation
//   plumbing is in place for the live queue to drive it without UI changes.
// =============================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** A record's sync state, surfaced as a badge on grids/cards. */
export type RecordSyncState = "synced" | "pending" | "cached_offline";

interface SyncCtx {
  /** navigator.onLine — false ⇒ records render as CACHED OFFLINE. */
  online: boolean;
  /** Count of records with a local mutation not yet flushed to /api/v1. */
  pendingCount: number;
  /** Mark a record key as having a pending (unflushed) local mutation. */
  markPending: (key: string) => void;
  /** Clear a record's pending mark (its mutation flushed / was discarded). */
  clearPending: (key: string) => void;
  /** Resolve the badge state for a given record key. */
  stateFor: (key: string) => RecordSyncState;
}

const Ctx = createContext<SyncCtx | null>(null);

function probeOnline(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.onLine === "boolean" ? navigator.onLine : true;
}

export function SyncProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState<boolean>(probeOnline);
  const [pending, setPending] = useState<Set<string>>(() => new Set());

  // Live connectivity — the same input core's SyncClient heartbeat reads.
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const markPending = useCallback((key: string) => {
    setPending((s) => {
      if (s.has(key)) return s;
      const next = new Set(s);
      next.add(key);
      return next;
    });
  }, []);

  const clearPending = useCallback((key: string) => {
    setPending((s) => {
      if (!s.has(key)) return s;
      const next = new Set(s);
      next.delete(key);
      return next;
    });
  }, []);

  const stateFor = useCallback<SyncCtx["stateFor"]>(
    (key) => {
      if (pending.has(key)) return "pending";
      if (!online) return "cached_offline";
      return "synced";
    },
    [pending, online],
  );

  const value = useMemo<SyncCtx>(
    () => ({ online, pendingCount: pending.size, markPending, clearPending, stateFor }),
    [online, pending, markPending, clearPending, stateFor],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSync(): SyncCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSync must be used within SyncProvider");
  return ctx;
}
